import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'antd';
import { logout, isLoggedIn, setSession, getUser } from '../../utils/auth';
import { AuthService } from '../../services/auth/AuthService';

const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
const COUNTDOWN_SECONDS = 60; // 1 minute warning before auto-logout

const SessionManager: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState(isLoggedIn());
  const [warningVisible, setWarningVisible] = useState(false);
  const warningVisibleRef = useRef(warningVisible);
  warningVisibleRef.current = warningVisible;

  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const idleTimer = useRef<NodeJS.Timeout | null>(null);
  const countdownInterval = useRef<NodeJS.Timeout | null>(null);

  const handleLogout = () => {
    setWarningVisible(false);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
    logout();
  };

  const resetIdleTimer = () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (countdownInterval.current) clearInterval(countdownInterval.current);
    
    setWarningVisible(false);
    setCountdown(COUNTDOWN_SECONDS);

    // Only start the idle timer if user is logged in
    if (isLoggedIn()) {
      idleTimer.current = setTimeout(() => {
        setWarningVisible(true);
      }, IDLE_TIMEOUT);
    }
  };

  const checkCurrentTokens = () => {
    console.log('🔍 === TOKEN DEBUG INFO ===');
    console.log('Access Token:', localStorage.getItem('token') ? 'EXISTS' : 'MISSING');
    console.log('Refresh Token:', localStorage.getItem('refreshToken') ? 'EXISTS' : 'MISSING');
    console.log('User:', localStorage.getItem('user') ? 'EXISTS' : 'MISSING');
    console.log('========================');
  };

  const handleExtendSession = async () => {
    const response = await AuthService.refreshToken();
  if (response.status === 'success' && response.token) {
    const currentUser = getUser();
    setSession(response.token, currentUser, response.refreshToken);
    setWarningVisible(false);
    resetIdleTimer();
  } else {
    handleLogout();
  } 
}
  
  const handleActivity = () => {
    if (!warningVisibleRef.current) {
      resetIdleTimer();
    }
  };

  const handleDebugClick = () => {
    checkCurrentTokens();
    setWarningVisible(true);
  };

  useEffect(() => {
    if (warningVisible) {
      setCountdown(COUNTDOWN_SECONDS); // Reset countdown when modal appears
      countdownInterval.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval.current!);
            handleLogout();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    }

    return () => {
      if (countdownInterval.current) {
        clearInterval(countdownInterval.current);
      }
    };
  }, [warningVisible]);

  useEffect(() => {
    const onAuthChange = () => setLoggedIn(isLoggedIn());
    window.addEventListener('authChange', onAuthChange);
    return () => window.removeEventListener('authChange', onAuthChange);
  }, []);

  useEffect(() => {
    const showDebugModal = () => handleDebugClick();
    window.addEventListener('showSessionDebugModal', showDebugModal);

    if (!loggedIn) {
      return () => {
        window.removeEventListener('showSessionDebugModal', showDebugModal);
      };
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => window.addEventListener(event, handleActivity));
    resetIdleTimer();

    return () => {
      window.removeEventListener('showSessionDebugModal', showDebugModal);
      events.forEach(event => window.removeEventListener(event, handleActivity));
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (countdownInterval.current) clearInterval(countdownInterval.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn]);

  if (!loggedIn) {
    return null;
  }

  return (
    <>
      <Modal
        open={warningVisible}
        title="Session Expiring Soon"
        closable={false}
        footer={null}
        centered
      >
        <p>Your session will expire soon due to inactivity.</p>
        <p style={{ color: 'red', fontWeight: 600 }}>
          Logging out in {countdown} second{countdown !== 1 ? 's' : ''}...
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleLogout}>Logout</Button>
          <Button type="primary" onClick={handleExtendSession}>Extend Session</Button>
        </div>
      </Modal>
      {/* The debug button will be moved to PublicInternshipList.tsx */}
    </>
  );
};

export default SessionManager;